/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { CreateFlightReportRequest } from '../models/create-flight-report-request';
import { FlightReportDetailedResponse } from '../models/flight-report-detailed-response';
import { FlightReportFileUploadRequest } from '../models/flight-report-file-upload-request';
import { FlightReportResponse } from '../models/flight-report-response';
import { GenericResponse } from '../models/generic-response';
import { GetFlightReportResponse } from '../models/get-flight-report-response';
import { UpdateFlightReportRequest } from '../models/update-flight-report-request';

/**
 * flight report apis
 */
@Injectable({ providedIn: 'root' })
export class FlightReportService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getAllFlightReport()` */
  static readonly GetAllFlightReportPath = '/api/v1/flight-reports';

  /**
   * Get list of flight reports.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllFlightReport()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllFlightReport$Response(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to the following fields:
       * - aircraftTailName
       * - tripNumber
       * - flightType
       *   - type
       *   - name
       */
      search?: string;

      /**
       * The trip date start from
       */
      from?: string;

      /**
       * The trip date end to
       */
      to?: string;

      /**
       * The flight type search in flight report
       */
      flightType?: string;

      /**
       * The aircraft search in flight report
       */
      aircraft?: string;

      /**
       * The created by search in flight report
       */
      createdBy?: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GetFlightReportResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      FlightReportService.GetAllFlightReportPath,
      'get'
    );
    if (params) {
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
      rb.query('search', params.search, {});
      rb.query('from', params.from, {});
      rb.query('to', params.to, {});
      rb.query('flightType', params.flightType, {});
      rb.query('aircraft', params.aircraft, {});
      rb.query('createdBy', params.createdBy, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GetFlightReportResponse>;
        })
      );
  }

  /**
   * Get list of flight reports.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAllFlightReport$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllFlightReport(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to the following fields:
       * - aircraftTailName
       * - tripNumber
       * - flightType
       *   - type
       *   - name
       */
      search?: string;

      /**
       * The trip date start from
       */
      from?: string;

      /**
       * The trip date end to
       */
      to?: string;

      /**
       * The flight type search in flight report
       */
      flightType?: string;

      /**
       * The aircraft search in flight report
       */
      aircraft?: string;

      /**
       * The created by search in flight report
       */
      createdBy?: number;
    },
    context?: HttpContext
  ): Observable<GetFlightReportResponse> {
    return this.getAllFlightReport$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<GetFlightReportResponse>
        ): GetFlightReportResponse => r.body
      )
    );
  }

  /** Path part for operation `createFlightReport()` */
  static readonly CreateFlightReportPath = '/api/v1/flight-reports';

  /**
   * create new flight report.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createFlightReport()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createFlightReport$Response(
    params: {
      body: CreateFlightReportRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<FlightReportResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      FlightReportService.CreateFlightReportPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<FlightReportResponse>;
        })
      );
  }

  /**
   * create new flight report.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createFlightReport$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createFlightReport(
    params: {
      body: CreateFlightReportRequest;
    },
    context?: HttpContext
  ): Observable<FlightReportResponse> {
    return this.createFlightReport$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<FlightReportResponse>): FlightReportResponse =>
          r.body
      )
    );
  }

  /** Path part for operation `getFlightReportById()` */
  static readonly GetFlightReportByIdPath = '/api/v1/flight-reports/{id}';

  /**
   * fetch flight report details.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getFlightReportById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getFlightReportById$Response(
    params: {
      /**
       * The flight report's id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<FlightReportDetailedResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      FlightReportService.GetFlightReportByIdPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<FlightReportDetailedResponse>;
        })
      );
  }

  /**
   * fetch flight report details.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getFlightReportById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getFlightReportById(
    params: {
      /**
       * The flight report's id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<FlightReportDetailedResponse> {
    return this.getFlightReportById$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<FlightReportDetailedResponse>
        ): FlightReportDetailedResponse => r.body
      )
    );
  }

  /** Path part for operation `confirmFlightReport()` */
  static readonly ConfirmFlightReportPath = '/api/v1/flight-reports/{id}';

  /**
   * confirm flight report.
   *
   * Confirm whether the flight report is saved
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `confirmFlightReport()` instead.
   *
   * This method doesn't expect any request body.
   */
  confirmFlightReport$Response(
    params: {
      /**
       * The flight report's id that needs to be updated
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GenericResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      FlightReportService.ConfirmFlightReportPath,
      'post'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GenericResponse>;
        })
      );
  }

  /**
   * confirm flight report.
   *
   * Confirm whether the flight report is saved
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `confirmFlightReport$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  confirmFlightReport(
    params: {
      /**
       * The flight report's id that needs to be updated
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<GenericResponse> {
    return this.confirmFlightReport$Response(params, context).pipe(
      map((r: StrictHttpResponse<GenericResponse>): GenericResponse => r.body)
    );
  }

  /** Path part for operation `updateFlightReport()` */
  static readonly UpdateFlightReportPath = '/api/v1/flight-reports/{id}';

  /**
   * update flight report.
   *
   * update flight report
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateFlightReport()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateFlightReport$Response(
    params: {
      /**
       * The flight report's id that needs to be updated
       */
      id: number;
      body: UpdateFlightReportRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<FlightReportDetailedResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      FlightReportService.UpdateFlightReportPath,
      'patch'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<FlightReportDetailedResponse>;
        })
      );
  }

  /**
   * update flight report.
   *
   * update flight report
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateFlightReport$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateFlightReport(
    params: {
      /**
       * The flight report's id that needs to be updated
       */
      id: number;
      body: UpdateFlightReportRequest;
    },
    context?: HttpContext
  ): Observable<FlightReportDetailedResponse> {
    return this.updateFlightReport$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<FlightReportDetailedResponse>
        ): FlightReportDetailedResponse => r.body
      )
    );
  }

  /** Path part for operation `downloadFlightReport()` */
  static readonly DownloadFlightReportPath = '/api/v1/flight-reports/report';

  /**
   * to download flight report details report.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `downloadFlightReport()` instead.
   *
   * This method doesn't expect any request body.
   */
  downloadFlightReport$Response(
    params?: {
      from?: string;
      to?: string;
      flightTypeId?: number;
      aircraft?: string;
      createdBy?: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Blob>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      FlightReportService.DownloadFlightReportPath,
      'get'
    );
    if (params) {
      rb.query('from', params.from, {});
      rb.query('to', params.to, {});
      rb.query('flightTypeId', params.flightTypeId, {});
      rb.query('aircraft', params.aircraft, {});
      rb.query('createdBy', params.createdBy, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'blob',
          accept:
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          context,
        })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<Blob>;
        })
      );
  }

  /**
   * to download flight report details report.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `downloadFlightReport$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  downloadFlightReport(
    params?: {
      from?: string;
      to?: string;
      flightTypeId?: number;
      aircraft?: string;
      createdBy?: number;
    },
    context?: HttpContext
  ): Observable<Blob> {
    return this.downloadFlightReport$Response(params, context).pipe(
      map((r: StrictHttpResponse<Blob>): Blob => r.body)
    );
  }

  /** Path part for operation `uploadFlightReportAttachments()` */
  static readonly UploadFlightReportAttachmentsPath =
    '/api/v1/flight-reports/{id}/attachments';

  /**
   * Upload Comment Attachments.
   *
   * Upload image attachments for a flight report comment
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `uploadFlightReportAttachments()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  uploadFlightReportAttachments$Response(
    params: {
      /**
       * The report id to which the file is to be attached
       */
      id: number;
      body: FlightReportFileUploadRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GenericResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      FlightReportService.UploadFlightReportAttachmentsPath,
      'post'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'multipart/form-data');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GenericResponse>;
        })
      );
  }

  /**
   * Upload Comment Attachments.
   *
   * Upload image attachments for a flight report comment
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `uploadFlightReportAttachments$Response()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  uploadFlightReportAttachments(
    params: {
      /**
       * The report id to which the file is to be attached
       */
      id: number;
      body: FlightReportFileUploadRequest;
    },
    context?: HttpContext
  ): Observable<GenericResponse> {
    return this.uploadFlightReportAttachments$Response(params, context).pipe(
      map((r: StrictHttpResponse<GenericResponse>): GenericResponse => r.body)
    );
  }

  /** Path part for operation `downloadFlightReportAttachments()` */
  static readonly DownloadFlightReportAttachmentsPath =
    '/api/v1/flight-reports/{id}/attachments/{file-id}';

  /**
   * download Comment Attachments.
   *
   * download image attachments for a flight report comment
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `downloadFlightReportAttachments()` instead.
   *
   * This method doesn't expect any request body.
   */
  downloadFlightReportAttachments$Response(
    params: {
      /**
       * The report id to which the file is to be downloaded
       */
      id: number;

      /**
       * The file id to be downloaded
       */
      'file-id': number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Blob>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      FlightReportService.DownloadFlightReportAttachmentsPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.path('file-id', params['file-id'], {});
    }

    return this.http
      .request(rb.build({ responseType: 'blob', accept: '*/*', context }))
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<Blob>;
        })
      );
  }

  /**
   * download Comment Attachments.
   *
   * download image attachments for a flight report comment
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `downloadFlightReportAttachments$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  downloadFlightReportAttachments(
    params: {
      /**
       * The report id to which the file is to be downloaded
       */
      id: number;

      /**
       * The file id to be downloaded
       */
      'file-id': number;
    },
    context?: HttpContext
  ): Observable<Blob> {
    return this.downloadFlightReportAttachments$Response(params, context).pipe(
      map((r: StrictHttpResponse<Blob>): Blob => r.body)
    );
  }
}
